// @file composable for accessibility settings
import { isRegistered } from '@@/bits/user_model'
import { useAccessibilitySettingsStore } from '@@/pinia/accessibility_settings_store'
import type { User, UserCamelCase } from '@@/types'
import { storeToRefs } from 'pinia'
import type { Ref } from 'vue'
import { getCurrentScope, onScopeDispose, watch } from 'vue'

type AccessibilitySettingsUser = User | UserCamelCase | null | undefined
interface UserSource {
  user: AccessibilitySettingsUser
}
interface UserSourceRegistry {
  sources: Map<symbol, UserSource>
}

type AccessibilitySettingsStore = ReturnType<typeof useAccessibilitySettingsStore>

const userSourceRegistries = new WeakMap<AccessibilitySettingsStore, UserSourceRegistry>()

const getUserSourceRegistry = (store: AccessibilitySettingsStore): UserSourceRegistry => {
  const existingRegistry = userSourceRegistries.get(store)
  if (existingRegistry != null) return existingRegistry

  const registry = { sources: new Map<symbol, UserSource>() }
  userSourceRegistries.set(store, registry)
  return registry
}

const syncStoreUser = (store: AccessibilitySettingsStore, registry: UserSourceRegistry): void => {
  let registeredSource: UserSource | undefined
  for (const source of registry.sources.values()) {
    if (source.user == null || !isRegistered(source.user)) continue
    if (registeredSource != null && source.user.id !== registeredSource.user?.id) {
      // Different registered users in one app scope means the caller state is
      // inconsistent. Do not let component mount order choose whose settings
      // are loaded; wait until the sources converge on one user.
      store.init(undefined)
      return
    }
    registeredSource = source
  }
  store.init(registeredSource?.user ?? undefined)
}

const useAccessibilitySettings = (user?: Ref<AccessibilitySettingsUser>) => {
  const accessibilitySettingsStore = useAccessibilitySettingsStore()
  // Some call sites (for example snackbar actions) execute outside a Vue
  // effect scope. They can consume the Pinia store, but must not
  // install process-lifetime watchers.
  if (user != null && getCurrentScope() != null) {
    const registry = getUserSourceRegistry(accessibilitySettingsStore)
    const sourceToken = Symbol('accessibility-settings-user-source')
    const stopWatchingUser = watch(
      () => {
        const currentUser = user.value
        return [
          currentUser?.id,
          currentUser?.registered,
          currentUser != null && 'registered_at' in currentUser ? currentUser.registered_at : undefined,
          currentUser != null && 'registeredAt' in currentUser ? currentUser.registeredAt?.valueOf() : undefined,
        ] as const
      },
      () => {
        registry.sources.set(sourceToken, {
          user: user.value,
        })
        syncStoreUser(accessibilitySettingsStore, registry)
      },
      { immediate: true },
    )
    onScopeDispose(() => {
      stopWatchingUser()
      registry.sources.delete(sourceToken)
      syncStoreUser(accessibilitySettingsStore, registry)
    })
  }

  const stateRefs = storeToRefs(accessibilitySettingsStore)
  const {
    toggleIsKeyboardShortcutsEnabled,
    toggleIsAppMessagesAutoDismissed,
    updateHighContrastModePreference,
    updateReducedMotionPreference,
    setStateUpdatesToBeLocallySaved,
  } = accessibilitySettingsStore

  return {
    ...stateRefs,

    toggleIsKeyboardShortcutsEnabled,
    toggleIsAppMessagesAutoDismissed,
    updateHighContrastModePreference,
    updateReducedMotionPreference,
    setStateUpdatesToBeLocallySaved,
  }
}

export { useAccessibilitySettings }
